import React, { useState, useEffect } from 'react';
import { 
  MdClose, 
  MdPhoto, 
  MdVideoLibrary, 
  MdDescription,
  MdContentCopy,
  MdCheck
} from 'react-icons/md';
import { getCookie } from '../api/cookie';
import { makeAuthenticatedFetch } from '../api/whatsapp.service';

interface UploadedFile {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document';
  size: number;
  url: string;
  uploadDate: string;
  fileHandle?: string;
  s3Key?: string;
  imageLoading?: boolean;
}

interface MediaSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  title?: string;
}

export default function MediaSelectionPopup({ 
  isOpen, 
  onClose, 
  onSelect, 
  title = "Select Media File" 
}: MediaSelectionPopupProps) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState('');

  // Fetch user media files
  const fetchUserMedia = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const phoneNumberId = sessionStorage.getItem('phoneNumberId');
      
      if (!phoneNumberId) {
        setError('No phone number ID found. Please ensure you are logged in.');
        return;
      }

      const response = await makeAuthenticatedFetch(`https://api.frontdesk24.com/api/user-media/${phoneNumberId}`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data && data.data.mediaItems) {
          // Transform API response to match our UploadedFile interface
          const apiFiles: UploadedFile[] = data.data.mediaItems.map((item: any) => {
            // Extract filename from s3Key or use mediaId
            let fileName = item.mediaId;
            if (item.s3Key) {
              const s3FileName = item.s3Key.split('/').pop();
              if (s3FileName) {
                const parts = s3FileName.split('-');
                if (parts.length > 2) {
                  fileName = parts.slice(2).join('-').replace(/\.(png|jpg|jpeg|gif|mp4|pdf|doc|docx)$/i, '');
                } else {
                  fileName = s3FileName;
                }
              }
            }
            
            return {
              id: item.mediaId,
              name: fileName,
              type: getFileTypeFromUrl(item.mediaUrl),
              size: 0,
              url: item.mediaUrl,
              uploadDate: item.createdAt,
              fileHandle: item.mediaId,
              s3Key: item.s3Key
            };
          });
          
          setFiles(apiFiles);
        } else {
          setFiles([]);
        }
      } else {
        setError('Failed to load media files');
      }
    } catch (error) {
      // Error: 'Error fetching user media:', error);
      setError('Error loading media files');
    } finally {
      setLoading(false);
    }
  };

  const getFileTypeFromUrl = (url: string): 'image' | 'video' | 'document' => {
    if (!url) return 'document';
    
    const extension = url.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return 'image';
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      return 'video';
    }
    return 'document';
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <MdPhoto className="h-6 w-6 text-green-500" />;
      case 'video':
        return <MdVideoLibrary className="h-6 w-6 text-blue-500" />;
      default:
        return <MdDescription className="h-6 w-6 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(''), 2000);
    } catch (error) {
      // Error: 'Failed to copy URL:', error);
    }
  };

  const handleSelect = (file: UploadedFile) => {
    onSelect(file.url);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserMedia();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchUserMedia}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12">
              <MdPhoto className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No media files found</p>
              <p className="text-sm text-gray-400">
                Upload files in the Media Files section to use them here
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map((file) => (
                <div 
                  key={file.id} 
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer border-2 border-transparent hover:border-blue-300"
                  onClick={() => handleSelect(file)}
                >
                  {/* File Preview */}
                  <div className="h-32 bg-white rounded-lg mb-3 flex items-center justify-center overflow-hidden relative">
                    {file.type === 'image' ? (
                      <>
                        {/* Image Loading Skeleton */}
                        {file.imageLoading !== false && (
                          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
                          </div>
                        )}
                        
                        {/* Actual Image */}
                        <img
                          src={file.url}
                          alt={file.name}
                          className="w-full h-full object-cover"
                          onLoad={() => {
                            setFiles(prevFiles => 
                              prevFiles.map(f => 
                                f.id === file.id ? { ...f, imageLoading: false } : f
                              )
                            );
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            setFiles(prevFiles => 
                              prevFiles.map(f => 
                                f.id === file.id ? { ...f, imageLoading: false } : f
                              )
                            );
                          }}
                        />
                      </>
                    ) : null}
                    <div className={`${file.type === 'image' ? 'hidden' : ''} flex items-center justify-center`}>
                      {getFileIcon(file.type)}
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(file.uploadDate)}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        file.type === 'image' ? 'bg-green-100 text-green-800' :
                        file.type === 'video' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {file.type.toUpperCase()}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(file.url);
                        }}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        title="Copy URL"
                      >
                        {copiedUrl === file.url ? (
                          <MdCheck className="h-4 w-4 text-green-500" />
                        ) : (
                          <MdContentCopy className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {files.length} media file{files.length !== 1 ? 's' : ''} available
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
